/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { MovementHistory }   from "@/models/scenario/movement.history";
import { Movement }          from "@/models/scenario/movement";
import { Train }             from "@/models/scenario/train";
import { StringColorMode }   from "@/screens/ide/stringviewer/three.string.viewer";
import { Scenario }          from "@/models/scenario/scenario";

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineBasicMaterial,
  LineSegments,
  Plane,
  WebGLRenderer
} from 'three';



/**
 *
 * @class TrainLinesBuilder
 *
 * this builder can make a single wireframe object for all the train lines.  This is a big boost
 * in performance, since there is no Javascript looping over hundreds of train objects.  Its just
 * one big geometry that lives in the video card.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class TrainLinesBuilder {

  /** timescale gets clipped so we don't go beyond simulation time */
  private  clippingPlane: Plane|null           = null;

  /** the geometric scale of the graph (vertical) */
  private    distanceScale: number             = 1.0;

  /** the geometric scale of the graph (horizontal) */
  private        timeScale: number             = 1.0;

  /** where the X data points start */
  private            dataX: number             = 0;

  /** where the Y data points start */
  private            dataY: number             = 0;

  /** the unscaled max time value */
  private        dataWidth: number             = 0;

  /** the unscaled max distance value */
  private       dataHeight: number             = 0;

  /** the (maybe timezone specific) scenario */
  private         scenario: Scenario|null       = null;

  /** the rendering pipeline */
  private         renderer: WebGLRenderer|null  = null;

  /** style of track coloring we are using */
  private        colorMode: StringColorMode     = StringColorMode.TRAIN;

  /** the style picker to use for tracks */
  private trackStylePicker: (move: Movement) => any = (move: Movement): any => {return 'black';};

  /** the style picker to use for trains */
  private trainStylePicker: (train: Train) => string = (train: Train): string => {return 'black';};

  /** standard constructor */
  constructor(init?: Partial<TrainLinesBuilder>) {

    Object.assign(this, init);
  }

  /** fetch the data start (horizontal) */
  public getX(): number {

    return this.dataX;
  }

  /** fetch the data start (vertical) */
  public getY(): number {

    return this.dataY;
  }

  /** fetch the data end (horizontal) */
  public getWidth(): number {

    return this.dataWidth;
  }

  /** fetch the data end (vertical) */
  public getHeight(): number {

    return this.dataHeight;
  }

  /** force the scenario */
  public setScenario(scenario: Scenario): TrainLinesBuilder {

    this.scenario = scenario;

    return this;
  }

  /** fetch the scenario */
  public getScenario(): Scenario {

    return this.scenario;
  }

  /** force the distance (vertical) geometric scaling */
  public setDistanceScale(scale: number): TrainLinesBuilder {

    this.distanceScale = scale;

    return this;
  }

  /** fetch the distance (vertical) geometric scaling */
  public getDistanceScale(): number {

    return this.distanceScale;
  }

  /** force the time (horizontal) geometric scaling */
  public setTimeScale(scale: number): TrainLinesBuilder {

    this.timeScale = scale;

    return this;
  }

  /** fetch the time (horizontal) geometric scaling */
  public getTimeScale(): number {

    return this.timeScale;
  }

  /** force the clipping extent */
  public setClippingPlane(clip: Plane): TrainLinesBuilder {

    this.clippingPlane = clip;

    return this;
  }

  /** fetch the clipping extent */
  public getClippingPlane(): Plane {

    return this.clippingPlane;
  }

  /** force the rendering pipeline */
  public setRenderer(pipeline: WebGLRenderer): TrainLinesBuilder {

    this.renderer = pipeline;

    return this;
  }

  /** fetch the rendering pipeline */
  public getRenderer(): WebGLRenderer {

    return this.renderer;
  }

  /** force the coloring mode */
  public setColorMode(mode: StringColorMode): TrainLinesBuilder {

    this.colorMode = mode;

    return this;
  }

  /** fetch the coloring mode */
  public getColorMode(): StringColorMode {

    return this.colorMode;
  }

  /** force the track style picker */
  public setTrackStylePicker(picker:  (move: Movement) => any): TrainLinesBuilder {

    this.trackStylePicker = picker;

    return this;
  }

  /** fetch the track style picker */
  public getTrackStylePicker(): (move: Movement) => any {

    return this.trackStylePicker;
  }

  /** force the train style picker */
  public setTrainStylePicker(picker:  (train: Train) => string): TrainLinesBuilder {

    this.trainStylePicker = picker;

    return this;
  }

  /** fetch the train style picker */
  public getTrainStylePicker(): (train: Train) => string {

    return this.trainStylePicker;
  }

  /** build the set of line segments as a complete geometry/mesh */
  public build(): LineSegments|null {

    let b: TrainLinesBuilder = this;

    if (!b.scenario || !b.clippingPlane || !b.renderer) {

      Logger.error(`[TrainLinesBuilder] build - no scenario, renderer or clipping plane. Aborting.`);
      return null;
    }

    if (b.scenario.trains().size == 0) {

      Logger.error(`[TrainLinesBuilder] build - no trains! Aborting.`);
      return null;
    }

    /* pre-scan for maximum number of vertices */

    let maxPoints: number               = 0;
    let    nMoves: number               = 0;

    {
      for (const train of b.scenario.trains().values()) {

        /* each movement will generate at most 2 line segments (4 vertices) */

        maxPoints += train._history._history.length * 4;
        nMoves    += train._history._history.length;
      }
    }

    /* get ready for line segment generation ... */

    let    geometry: BufferGeometry  = new BufferGeometry();
    let   positions: Float32Array    = new Float32Array(maxPoints * 3);
    let      colors: Float32Array    = new Float32Array(maxPoints * 3);
    let     indices: number[]        = [];
    let      vIndex: number          = 0;
    let      cIndex: number          = 0;
    let      pIndex: number          = 0;

    geometry.name = 'graph';

    /* create the plot ... */

    /* walk through all the train movements */

    const trains: Map<number, Train> = b.scenario.trains();
    let     maxX: number             = 0;
    let     maxY: number             = 0;
    let     minX: number             = Number.POSITIVE_INFINITY;
    let     minY: number             = Number.POSITIVE_INFINITY;

    let   colorConverter             = new Color('#000000');

    for (const train of trains.values()) {

      const history: MovementHistory = train._history;
      const   moves: Movement[]      = history._history;
      const   start: number          = 0;
      const     end: number          = moves.length - 1;
      let         i: number          = 0;

      /*
       * Figure out the line color for train coloring mode, if we are in train
       * coloring mode, then we can just use this color for all segments.
       *
       */

      const trainColor: string = b.trainStylePicker(train);

      if ((start === end) || (moves.length === 0)) {

        continue;
      }

      colorConverter.set(trainColor);

      /* walk through the moves for this train */

      for (i = start; i <= end; i++) {

        const move: Movement = moves[i];

        if (b.colorMode === StringColorMode.TRACK) {

          /* in track coloring mode, we have to color  each segment */

          colorConverter.set(b.trackStylePicker(move));
        }

        /* draw the lines for this movement */

        const lines: any[] = move.lines();

        let j: number              = 0;
        let consistMarked: boolean = false;

        for (j = 0; j < lines.length; j++) {

          let [x1, y1, x2, y2] = lines[j];

          if (x2 < x1) {

            /* Shark still looks fake. */

            const tmp: number = x1;
            x1 = x2;
            x2 = tmp;

          }

          /* draw the segment */

          let sx: number = x1 / 3600.0;
          let sy: number = y1;
          let ex: number = x2 / 3600.0;
          let ey: number = y2;

          if (isNaN(sx)) {
            sx = 0;
          }
          if (isNaN(sy)) {
            sy = 0;
          }
          if (isNaN(ex)) {
            ex = 0;
          }
          if (isNaN(ey)) {
            ey = 0;
          }

          /* set the outer corner of the graph bounding box */

          if (sx > maxX) {
            maxX = sx;
          }

          if (ex > maxX) {
            maxX = ex;
          }

          if (sy > maxY) {
            maxY = sy;
          }

          if (ey > maxY) {
            maxY = ey;
          }

          /* set the inner corner of the graph bounding box */

          if (sx < minX) {
            minX = sx;
          }

          if (ex < minX) {
            minX = ex;
          }

          if (sy < minY) {
            minY = sy;
          }

          if (ey < minY) {
            minY = ey;
          }

          /* push this segment */

          positions[vIndex]     = sx;
          positions[vIndex + 1] = sy;
          positions[vIndex + 2] = 0;

          positions[vIndex + 3] = ex;
          positions[vIndex + 4] = ey;
          positions[vIndex + 5] = 0;

          indices.push(pIndex);
          indices.push(pIndex + 1);

          vIndex += 6;
          pIndex += 2;

          /* push the color for this segment */

          colors[cIndex]     = colorConverter.r;
          colors[cIndex + 1] = colorConverter.g;
          colors[cIndex + 2] = colorConverter.b;

          colors[cIndex + 3] = colorConverter.r;
          colors[cIndex + 4] = colorConverter.g;
          colors[cIndex + 5] = colorConverter.b;

          cIndex += 6;

          /*
           * if this movement is one where a consist change happened, we want to add an indicator, so we
           * draw a triangle in the center of segment (to indicate a "Delta"). We don't try to change the
           * color (say darker/lighter version of the color), because consist changes may be back to back,
           * and colors get assigned to train types.
           *
           */

          if ( !consistMarked &&
               (move._id != history._history[0]._id) &&
               (move._parent._consistChanges.find(x => x.move._id == move._id)) ) {

            consistMarked = true;

            /* this movement is where a consist change is happening */

            let cx: number = (sx + ex) / 2;
            let cy: number = (sy + ey) / 2;
            let lx: number = (history._tMax  / 3600) * .001;
            let ly: number = history._dMax * .001;

            let v1: {x: number, y: number} = {x:  0.0, y: -ly};
            let v2: {x: number, y: number} = {x:  lx, y: ly};
            let v3: {x: number, y: number} = {x: -lx, y: ly};

            /* side a */

            positions[vIndex]     = v1.x + cx -lx*2.5;
            positions[vIndex + 1] = v1.y + cy;
            positions[vIndex + 2] = 0;

            positions[vIndex + 3] = v2.x + cx -lx*2.5;
            positions[vIndex + 4] = v2.y + cy;
            positions[vIndex + 5] = 0;

            indices.push(pIndex);
            indices.push(pIndex + 1);

            vIndex += 6;
            pIndex += 2;

            /* push the color for this segment */

            colors[cIndex]     = colorConverter.r;
            colors[cIndex + 1] = colorConverter.g;
            colors[cIndex + 2] = colorConverter.b;

            colors[cIndex + 3] = colorConverter.r;
            colors[cIndex + 4] = colorConverter.g;
            colors[cIndex + 5] = colorConverter.b;

            cIndex += 6;

            /* side b */

            positions[vIndex]     = v2.x + cx -lx*2.5;
            positions[vIndex + 1] = v2.y + cy;
            positions[vIndex + 2] = 0;

            positions[vIndex + 3] = v3.x + cx -lx*2.5;
            positions[vIndex + 4] = v3.y + cy;
            positions[vIndex + 5] = 0;

            indices.push(pIndex);
            indices.push(pIndex + 1);

            vIndex += 6;
            pIndex += 2;

            /* push the color for this segment */

            colors[cIndex]     = colorConverter.r;
            colors[cIndex + 1] = colorConverter.g;
            colors[cIndex + 2] = colorConverter.b;

            colors[cIndex + 3] = colorConverter.r;
            colors[cIndex + 4] = colorConverter.g;
            colors[cIndex + 5] = colorConverter.b;

            cIndex += 6;

            /* side c */

            positions[vIndex]     = v3.x + cx -lx*2.5;
            positions[vIndex + 1] = v3.y + cy;
            positions[vIndex + 2] = 0;

            positions[vIndex + 3] = v1.x + cx -lx*2.5;
            positions[vIndex + 4] = v1.y + cy;
            positions[vIndex + 5] = 0;

            indices.push(pIndex);
            indices.push(pIndex + 1);

            vIndex += 6;
            pIndex += 2;

            /* push the color for this segment */

            colors[cIndex]     = colorConverter.r;
            colors[cIndex + 1] = colorConverter.g;
            colors[cIndex + 2] = colorConverter.b;

            colors[cIndex + 3] = colorConverter.r;
            colors[cIndex + 4] = colorConverter.g;
            colors[cIndex + 5] = colorConverter.b;

            cIndex += 6;

          }

          /* now handle the indicators on either end of the line segment ... */

          const      t1: number = move._arrival / 3600.0;
          const      t2: number = move._departure / 3600.0;

          /* get the arrow location for this segment */

          if (j === 0) {

            /* this is the incoming segment from the previous move */

            if ((t2 !== t1) || (i === start)) {

              if (i === start) {

                /* draw the network entry maker */

              } else {

                /* draw the arrival marker */

              }

            }

          }

          if (i === end) {

            /* draw the exit network marker */

          } else {

            /* draw the departure marker */

          }

        } /* end of segment walk within a move */

      } /* end of move walk */

    } /* end of train walk */

    /* set the content bound */

    b.dataWidth  = maxX;
    b.dataHeight = maxY;
    b.dataX      = minX;
    b.dataY      = minY;

    maxX *= b.timeScale;
    maxY *= b.distanceScale;

    const nTrains: number = b.scenario.trains().size;

    Logger.debug('[TrainLinesBuilder] build - world size: max x: ', maxX, ' max y: ', maxY);
    Logger.debug('[TrainLinesBuilder] build - screen size: max x: ', b.renderer.domElement.getBoundingClientRect().width, ' max y: ', b.renderer.domElement.getBoundingClientRect().height);
    Logger.debug('[TrainLinesBuilder] build - world size: min x: ', minX, ' min y: ', minY);
    Logger.debug('[TrainLinesBuilder] build - rendering count: trains: ', nTrains, ' moves: ', nMoves, ' segs: ', pIndex / 2);

    b.clippingPlane.constant = b.dataWidth * b.timeScale;

    /* create the buffered geometry for "one big line" that represents all the train movement lines */

    let material: LineBasicMaterial = new LineBasicMaterial( {
      color: 0xffffff,
      vertexColors: true,
      clippingPlanes: [b.clippingPlane]
    });

    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    geometry.setAttribute( 'color', new BufferAttribute( colors, 3 ));

    /* all done! */

    return new LineSegments(geometry, material);
  }
}
